import { inject, Injectable, signal } from '@angular/core';
import { doc, docData, DocumentReference, Firestore } from '@angular/fire/firestore';
import { EvalueeFeatureFlags, PortalFeatures, PortalSecuritySettings } from '@career-scope/models';
import { BehaviorSubject, Subscription, tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class FeaturesSecurityService {
  firestore = inject(Firestore);

  private portalSecuritySettingsSubject = new BehaviorSubject<PortalSecuritySettings | null>(null);
  private evalueeFeatureFlagsSubject = new BehaviorSubject<EvalueeFeatureFlags | null>(null);
  private autoLogout = new BehaviorSubject<boolean>(false);
  private audioEnabled = new BehaviorSubject<boolean>(false);
  private exerciseTimeModifier = new BehaviorSubject<number>(1);
  private untimed = new BehaviorSubject<boolean>(false);
  private showTimer = new BehaviorSubject<boolean>(true);
  private portalFeaturesSubscription: Subscription | null = null;
  isHighContrastMode = signal(false);

  portalFeatures = signal<PortalFeatures | null>(null);

  updateFeaturesSecurityInfo(portalSecuritySettings: PortalSecuritySettings, evalueeFeatureFlags: EvalueeFeatureFlags) {
    this.portalSecuritySettingsSubject.next(portalSecuritySettings);
    this.evalueeFeatureFlagsSubject.next(evalueeFeatureFlags);

    // This will retrigger the idle check if the autoLogout property gets 'updated'
    const autoLogout = portalSecuritySettings.autoLogout || false;
    if (this.autoLogout.value !== autoLogout) {
      this.autoLogout.next(portalSecuritySettings.autoLogout || false);
    }
  }

  clearFeaturesSecurityInfo() {
    this.portalSecuritySettingsSubject.next(null);
    this.evalueeFeatureFlagsSubject.next(null);
    this.autoLogout.next(false);
    this.portalFeaturesSubscription?.unsubscribe();
    this.portalFeatures.set(null);
  }

  portalSecuritySettings() {
    return this.portalSecuritySettingsSubject;
  }

  evalueeFeatureFlags() {
    return this.evalueeFeatureFlagsSubject;
  }

  getAutoLogout() {
    return this.autoLogout;
  }

  getAudioEnabled() {
    return this.audioEnabled;
  }

  setAudioEnabled(enabled: boolean) {
    const audioEnabled = this.audioEnabled.value;
    if (audioEnabled !== enabled) {
      this.audioEnabled.next(enabled);
    }
  }

  getExerciseTimeModifier() {
    return this.exerciseTimeModifier;
  }

  setExerciseTimeModifier(modifier: number) {
    const exerciseTimeModifier = this.exerciseTimeModifier.value;
    if (exerciseTimeModifier !== modifier) {
      console.log('setting exercise time modifier', modifier);
      this.exerciseTimeModifier.next(modifier);
    }
  }

  getUntimed() {
    return this.untimed;
  }

  setUntimed(untimed: boolean) {
    const currentUntimed = this.untimed.value;
    if (currentUntimed !== untimed) {
      this.untimed.next(untimed);
    }
  }

  getShowTimer() {
    return this.showTimer;
  }

  setShowTimer(showTimer: boolean) {
    const currentShowTimer = this.showTimer.value;
    if (currentShowTimer !== showTimer) {
      this.showTimer.next(showTimer);
    }
  }

  loadPortalFeatures(portalId: string) {
    if (this.portalFeatures()?.id === portalId) {
      return;
    }

    console.log('loading portal features', portalId);

    const portalFeaturesDocRef = doc(this.firestore, `portalFeatures/${portalId}`) as DocumentReference<PortalFeatures>;
    this.portalFeaturesSubscription = docData<PortalFeatures>(portalFeaturesDocRef).pipe(
      tap(portalFeatures => {
        if (portalFeatures) {
          this.portalFeatures.set(portalFeatures);
        } else {
          this.portalFeatures.set({ id: portalId, profileQuestions: ['gender'], lastModifiedDate: new Date(Date.now()) });
        }
      })
    ).subscribe();
  }
}
